"use client";

import { animate, motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useParams, useRouter } from "next/navigation";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import SplitText from "../ui/SplitText";

export default function HeroSection() {
  const { t } = useTranslation();
  const router = useRouter();
  const params = useParams<{ lang?: string }>();
  const scrollAnimationRef = useRef<ReturnType<typeof animate> | null>(null);
  const shouldReduceMotion = useReducedMotion();

  const langParam = params?.lang;
  const lang =
    typeof langParam === "string"
      ? langParam
      : Array.isArray(langParam)
        ? langParam[0]
        : "en";

  const journeyRoute = `/${lang}/projects#journey`;
  const heroName = t("hero.name", { defaultValue: "MOWA" });
  const handlePortfolioScroll = useCallback(() => {
    const section = document.getElementById("portfolio");

    if (section) {
      const targetY = section.getBoundingClientRect().top + window.scrollY - 36;
      if (shouldReduceMotion) {
        window.scrollTo(0, targetY);
        return;
      }

      scrollAnimationRef.current?.stop();
      scrollAnimationRef.current = animate(window.scrollY, targetY, {
        duration: 0.9,
        ease: [0.2, 0.9, 0.25, 1],
        onUpdate: (value) => window.scrollTo(0, value),
      });
      return;
    }

    router.push(`/${lang}#portfolio`);
  }, [lang, router, shouldReduceMotion]);

  useEffect(() => {
    return () => scrollAnimationRef.current?.stop();
  }, []);

  return (
    <section className="relative flex min-h-[80vh] justify-center overflow-hidden rounded-xl border border-black/20 bg-white p-6 dark:border-white/25 dark:bg-neutral-950 md:p-10">
      <div className="grid min-h-[600px] items-center justify-center gap-8">
        <motion.div
          initial={shouldReduceMotion ? false : { opacity: 0, y: 20 }}
          animate={shouldReduceMotion ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="font-display mb-3 text-sm uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400">
            {t("hero.eyebrow", { defaultValue: "Project portfolio" })}
          </p>
          <h1 className="font-display mb-5 w-fit text-5xl leading-[0.95] tracking-[-0.045em] text-neutral-950 dark:text-white md:text-7xl">
            {shouldReduceMotion ? (
              heroName
            ) : (
              <SplitText
                text={heroName}
                delay={50}
                duration={1.25}
                ease="power3.out"
                splitType="chars"
                from={{ opacity: 0, y: 40 }}
                to={{ opacity: 1, y: 0 }}
                threshold={0.1}
                rootMargin="-100px"
                textAlign="center"
              />
            )}
          </h1>

          <p className="font-display max-w-3xl text-2xl leading-tight tracking-[-0.035em] text-neutral-950 dark:text-white md:text-4xl">
            {t("hero.headline", {
              defaultValue: "Digital products across web, mobile, backend, and design systems.",
            })}
          </p>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-neutral-700 dark:text-neutral-300 md:text-lg">
            {t("hero.subtitle", {
              defaultValue:
                "A Switzerland-based portfolio for production-ready software, e-commerce, and design-led product systems.",
            })}
          </p>

          <div className="mt-7 flex w-fit gap-3 ">
            <button
              onClick={handlePortfolioScroll}
              className="font-display inline-flex items-center gap-2 rounded-lg border border-black bg-black px-5 py-3 text-sm font-medium uppercase tracking-[0.08em] text-white transition hover:border-green-600 hover:bg-green-600 dark:border-white dark:bg-white dark:text-black dark:hover:border-green-600 dark:hover:bg-green-600 dark:hover:text-white"
            >
              {t("hero.primaryCta", { defaultValue: "Projects" })}
              <ArrowUpRight size={16} />
            </button>
            <button
              onClick={() => router.push(journeyRoute)}
              className="font-display rounded-lg border border-black/20 px-5 py-3 text-sm font-medium uppercase tracking-[0.08em] transition hover:border-green-600 hover:text-green-600 dark:border-white/25 dark:hover:border-green-500 dark:hover:text-green-400"
            >
              {t("hero.secondaryCta", { defaultValue: "Background" })}
            </button>
          </div>
          {/*<div className="relative w-96 my-10">
            {pillars.map((text, index) => (
              <TextPressure
                key={index}
                text={text}
                flex
                alpha={false}
                stroke={false}
                width={true}
                weight={true}
                italic={false}
                textColor="gray"
                strokeColor="#5227FF"
                minFontSize={36}
                activationInset={44}
              />
            ))}
          </div>*/}
        </motion.div>
      </div>
    </section>
  );
}
